import axios from "axios";
import { addDays, addHours, addMinutes, addMonths, addYears } from "date-fns";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AsyncFilterLoadOptions,
  AxiosRequestConfigWithSignal,
  ColumnDefinition,
  DatasetProviderProps,
  DatasetSettings,
  FetchRowsParams,
  FetchRowsResult,
  FilterDefinition,
  FilterGroup,
  ItemForDetailParams,
  OptionType,
  Sort,
} from "../types";
import {
  DataHubClient,
  DataHubDatasetRequestParams,
  ExportFormat,
} from "./DataHubClient";
import { JSONObject } from "@spring-global/spring-datasources/lib/src/Types";
import { cloneDeep } from "lodash";
import JSZip from "jszip";

export interface ExportProgress {
  isExporting: boolean;
  format: "csv" | "xlsx" | null;
  totalRows: number;
  downloadedRows: number;
  downloadedBytes: number;
  exportError: string | null;
  estimatedTotalBytes: number;
  percentage: number;
  startTime: number | null;
  estimatedTimeRemaining: number | null;
}

export interface DatasetContextValue {
  filtersDefinition: FilterGroup[];
  getFilterValue: (code: string) => unknown;
  setFilterValue: (code: string, value: unknown) => void;
  getFiltersStateParam: (code: string) => unknown;
  setFiltersStateParam: (code: string, value: unknown) => void;
  requiredFiltersSatisfied: boolean;
  requiredFiltersWarningMessage: string;
  filtersState: Record<string, unknown>;
  filtersDataSource: Record<string, unknown[]>;
  setSort: (sort: Sort | undefined) => void;
  sort: Sort | undefined;
  columnDefinition: ColumnDefinition[];
  count: number | undefined;
  filteredCount: number | undefined;
  hasError: boolean;
  dataSourceId: string;
  isLoading: boolean;
  traceId: string;
  fetchRows: (params: FetchRowsParams) => Promise<FetchRowsResult>;
  resetFilters: () => void;
  filtersVersion: number;
  settings?: DatasetSettings;
  asyncFilterLoadOptions: Record<string, AsyncFilterLoadOptions>;
  selectedItemForDetail: ItemForDetailParams | null;
  setSelectedItemForDetail: (value: ItemForDetailParams | null) => void;
  exportData: (format: ExportFormat) => Promise<void>;
  cancelExport: () => void;
  dismissExportError: () => void;
  exportProgress: ExportProgress;
  isDefinitionLoaded: boolean;
  refreshData: () => void;
}

const unitMap: Record<string, (date: Date, amount: number) => Date> = {
  d: addDays,
  h: addHours,
  m: addMinutes,
  M: addMonths,
  y: addYears,
};

export const DatasetContext = createContext<DatasetContextValue | null>(null);

export function useDataset(): DatasetContextValue {
  const ctx = useContext(DatasetContext);
  if (!ctx) {
    throw new Error("useDataset must be used within DatasetProvider");
  }
  return ctx;
}

export function DatasetProvider(props: DatasetProviderProps) {
  const [settings, setSettings] = useState<DatasetSettings>();
  const [filtersDataSource, setFiltersDataSource] = useState<
    Record<string, unknown[]>
  >({});
  const [filtersDefinition, setFiltersDefinition] = useState<FilterGroup[]>([]);
  const [filtersState, setFiltersState] = useState<Record<string, unknown>>({});
  const [_filtersStateParam, _setFiltersStateParam] = useState<
    Record<string, unknown>
  >({});
  const [columnDefinition, setColumnDefinition] = useState<ColumnDefinition[]>(
    [],
  );

  const [
    selectedItemForDetail,
    setSelectedItemForDetail,
  ] = useState<ItemForDetailParams | null>(null);

  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    isExporting: false,
    format: null,
    totalRows: 0,
    downloadedRows: 0,
    downloadedBytes: 0,
    estimatedTotalBytes: 0,
    percentage: 0,
    startTime: null,
    estimatedTimeRemaining: null,
    exportError: null,
  });

  const exportAbortControllerRef = useRef<AbortController | null>(null);
  const exportResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const cacheTokenKey = `dataset_${props.dataSourceId}_requestId`;

  const [requestId, setRequestId] = useState<string | undefined>(
    () => localStorage.getItem(cacheTokenKey) || undefined,
  );

  const refreshData = useCallback(() => {
    const newId = crypto.randomUUID();
    localStorage.setItem(cacheTokenKey, newId);
    setRequestId(newId);
    setFiltersDataSource({});
  }, [cacheTokenKey]);

  // Ctrl+F5 / Ctrl+Shift+R: set a fresh _requestId before the hard reload
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isHardRefresh =
        (e.ctrlKey || e.metaKey) &&
        (e.key === "F5" || (e.shiftKey && e.key === "r"));
      if (isHardRefresh) {
        localStorage.setItem(cacheTokenKey, crypto.randomUUID());
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [cacheTokenKey]);

  const [sort, setSort] = useState<Sort | undefined>();
  const [count, setCount] = useState<number | undefined>(0);
  const [filteredCount, setFilteredCount] = useState<number | undefined>(0);
  const [hasError, setError] = useState(false);
  const [traceId, setTraceId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDefinitionLoaded, setDefinitionLoaded] = useState(false);
  const [requiredFiltersSatisfied, setRequiredFiltersSatisfied] = useState(
    false,
  );
  const [
    requiredFiltersWarningMessage,
    setRequiredFiltersWarningMessage,
  ] = useState("");

  const abortControllerRef = useRef<AbortController | null>(null);

  const getFiltersStateParam = useCallback(
    (code: string): unknown => _filtersStateParam[code] ?? "",
    [_filtersStateParam],
  );

  const setFiltersStateParam = useCallback((code: string, value: unknown) => {
    _setFiltersStateParam((prev) => ({
      ...prev,
      [code]: value,
    }));
  }, []);

  const getFilterValue = useCallback((code: string) => filtersState[code], [
    filtersState,
  ]);

  const setFilterValue = useCallback((code: string, value: unknown) => {
    setFiltersState((prev) => ({
      ...prev,
      [code]: value,
    }));
  }, []);

  const parseDateExpression = (
    value: Date | number | string,
  ): Date | undefined => {
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);
    if (typeof value !== "string") return undefined;

    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    const match = value.match(/^([+-])(\d+)([a-zA-Z])$/);
    if (!match) return undefined;

    const [, sign, amount, unit] = match;
    const fn = unitMap[unit as keyof typeof unitMap];
    if (!fn) return undefined;

    const delta = sign === "-" ? -Number(amount) : Number(amount);
    return fn(new Date(), delta);
  };

  const parseInitialValue = useCallback((filter: FilterDefinition): unknown => {
    const value = filter.initialValue;
    if (value == null) return undefined;

    switch (filter.dataType) {
      case "number": {
        const num = Number(value);
        return isNaN(num) ? undefined : num;
      }

      case "string[]":
        return Array.isArray(value)
          ? value.map((v) => String(v ?? "").trim())
          : [];

      case "number[]":
        return Array.isArray(value)
          ? value.map(Number).filter((n) => !isNaN(n))
          : [];

      case "date":
        return parseDateExpression(value as Date | number | string);

      case "date[]":
        if (Array.isArray(value) && value.length >= 2) {
          return [parseDateExpression(value[0]), parseDateExpression(value[1])];
        }
        return [];
      default:
        return String(value ?? "").trim();
    }
  }, []);

  const updateFiltersInitialValues = useCallback(
    (fg: FilterGroup[] | undefined): void => {
      if (!Array.isArray(fg) || fg.length === 0) return;

      const initialValues: Record<string, unknown> = {};
      fg.flatMap((g) => g.filters).forEach((f) => {
        if (f.initialValue !== undefined) {
          initialValues[f.code] = parseInitialValue(f);
        }
      });

      setFiltersState((prev) => ({
        ...prev,
        ...initialValues,
      }));
    },
    [parseInitialValue],
  );

  const updateSettings = (s: DatasetSettings | undefined): void => {
    const value: DatasetSettings = s ?? {};
    value.grid = value.grid ?? {};
    value.grid.cacheBlockSize = value.grid.cacheBlockSize ?? 100;
    value.grid.maxBlocksInCache = value.grid.maxBlocksInCache ?? 10;
    setSettings(value);
  };

  const handleRequestError = (e: unknown): void => {
    if (axios.isAxiosError(e) && e.code === "ERR_CANCELED") return;

    setError(true);
    if (axios.isAxiosError(e)) {
      setTraceId(e.response?.data?.traceId || "");
    }
  };

  const resetFilters = useCallback(() => {
    setFiltersState({});
    updateFiltersInitialValues(filtersDefinition);
  }, [filtersDefinition, updateFiltersInitialValues]);

  const isEmpty = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "string") return value.trim().length === 0;
    return value == null;
  };

  const sanitizedFiltersState = useMemo(() => {
    let newState = cloneDeep(filtersState);

    const allFilters = filtersDefinition.flatMap((f) => f.filters);

    allFilters
      .filter((f) => f.type === "search")
      .forEach((f) => {
        const val = newState[f.code];
        if (typeof val === "string") {
          const minLen =
            typeof f.args?.minLength === "number" ? f.args.minLength : 3;
          if (val.trim().length < minLen) {
            delete (newState as Record<string, unknown>)[f.code];
          }
        }
      });

    allFilters
      .filter((f) => f.type === "asyncMultiSelect")
      .forEach((f) => {
        const val = newState[f.code];
        if (Array.isArray(val)) {
          newState[f.code] = val.map((v: OptionType) => v.value);
        }
      });

    allFilters
      .filter((f) => f.type === "multiSelect")
      .forEach((f) => {
        const val = newState[f.code];
        if (Array.isArray(val)) {
          newState[f.code] = val.map((v: any) =>
            typeof v === "object" && v !== null && "id" in v ? v.id : v,
          );
        }
      });

    allFilters
      .filter((f) => f.type === "dateRange")
      .forEach((f) => {
        const val = newState[f.code];
        if (!Array.isArray(val)) return;
        newState[f.code] = (val as [string, string]).map(
          (v: string, index: number) => {
            const d = new Date(v);
            const year = d.getFullYear();
            const month = d.getMonth();
            const day = d.getDate();
            if (index === 0) {
              return new Date(
                Date.UTC(year, month, day, 0, 0, 0, 0),
              ).toISOString();
            } else {
              return new Date(
                Date.UTC(year, month, day, 23, 59, 59, 0),
              ).toISOString();
            }
          },
        );
      });

    return Object.fromEntries(
      Object.entries(newState).filter(([_, v]) => !isEmpty(v)),
    );
  }, [filtersDefinition, filtersState]);

  const [debouncedFilters, setDebouncedFilters] = useState(
    sanitizedFiltersState,
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(sanitizedFiltersState)) {
          return prev;
        }
        return sanitizedFiltersState;
      });
    }, 500);

    return () => clearTimeout(t);
  }, [sanitizedFiltersState]);

  const [filtersVersion, setFiltersVersion] = useState(0);

  useEffect(() => {
    setFiltersVersion((v) => v + 1);
  }, [debouncedFilters, requestId]);

  useEffect(() => {
    if (!isDefinitionLoaded) {
      return;
    }

    const reqOneOf = settings?.filter?.requirements?.oneOf ?? [];

    const hasMinimumFilters =
      !Array.isArray(reqOneOf) ||
      reqOneOf.length === 0 ||
      reqOneOf.some((r) => !isEmpty(filtersState[r]));

    setRequiredFiltersSatisfied(hasMinimumFilters);

    if (hasMinimumFilters) {
      setRequiredFiltersWarningMessage("");
      return;
    }

    const filterNames = filtersDefinition
      .flatMap((f) => f.filters)
      .filter((f) => reqOneOf?.includes(f.code))
      .map((f) => `"${f.name}"`)
      .join(", ");

    const warningMessage =
      settings?.filter?.requirements?.warningMessage ??
      `Please, select at least one of the filters: ${filterNames}.`;

    setRequiredFiltersWarningMessage(warningMessage);
  }, [filtersDefinition, filtersState, settings, isDefinitionLoaded]);

  const fetchRows = useCallback(
    async ({ startRow, endRow, sort: paramSort }: FetchRowsParams) => {
      const previousController = abortControllerRef.current;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(false);

      // Very small delay before aborting to avoid CORS race condition
      // but not too long to avoid duplicate requests
      if (previousController) {
        setTimeout(() => previousController.abort(), 5);
      }

      try {
        const top = endRow - startRow;
        const skip = startRow;
        const currentSort = paramSort ?? sort;
        const params: DataHubDatasetRequestParams = {
          publicId: props.dataSourceId,
          top,
          skip,
          sortColumn: currentSort?.columnId,
          sortDirection: currentSort?.direction,
          filtersState: debouncedFilters,
          _requestId: requestId,
        };

        const res = !requiredFiltersSatisfied
          ? { count: 0, filteredCount: 0, rows: [] }
          : await DataHubClient.getDataset(params, {
              signal: controller.signal,
            } as AxiosRequestConfigWithSignal);

        setCount(res.count);
        setFilteredCount(res.filteredCount);

        return {
          rows: res.rows ?? [],
          total: res.filteredCount ?? 0,
        };
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.code === "ERR_CANCELED") {
          return { rows: [], total: 0, canceled: true };
        }

        handleRequestError(e);
        return { rows: [], total: 0 };
      } finally {
        setIsLoading(false);
      }
    },
    [
      sort,
      props.dataSourceId,
      debouncedFilters,
      requiredFiltersSatisfied,
      requestId,
    ],
  );

  const fetchAsyncFilterData = useCallback(
    async (
      datasourceId: string,
      search: string,
      skip: number,
      bufferSize: number,
      args: JSONObject,
      signal?: AbortSignal,
    ) => {
      const payload = {
        _id: datasourceId,
        _requestId: requestId,
        args: {
          search,
          skip,
          top: bufferSize,
          ...(args ?? {}),
        },
      };
      const res = await DataHubClient.getAsyncFilterData(payload, { signal });
      return { data: res?.rows ?? [] };
    },
    [requestId],
  );

  const cancelExport = useCallback(() => {
    if (exportAbortControllerRef.current) {
      exportAbortControllerRef.current.abort();
      exportAbortControllerRef.current = null;
    }
    if (exportResetTimeoutRef.current) {
      clearTimeout(exportResetTimeoutRef.current);
      exportResetTimeoutRef.current = null;
    }
    setExportProgress({
      isExporting: false,
      format: null,
      totalRows: 0,
      downloadedRows: 0,
      downloadedBytes: 0,
      estimatedTotalBytes: 0,
      percentage: 0,
      startTime: null,
      estimatedTimeRemaining: null,
      exportError: null,
    });
  }, []);

  const dismissExportError = useCallback(() => {
    setExportProgress((prev) => ({ ...prev, exportError: null }));
  }, []);

  useEffect(() => {
    return () => {
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }
      if (exportResetTimeoutRef.current) {
        clearTimeout(exportResetTimeoutRef.current);
      }
    };
  }, []);

  const exportData = useCallback(
    async (format: ExportFormat): Promise<void> => {
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }

      if (exportResetTimeoutRef.current) {
        clearTimeout(exportResetTimeoutRef.current);
        exportResetTimeoutRef.current = null;
      }

      const abortController = new AbortController();
      exportAbortControllerRef.current = abortController;

      const startTime = Date.now();
      setExportProgress({
        isExporting: true,
        format,
        totalRows: 0,
        downloadedRows: 0,
        downloadedBytes: 0,
        estimatedTotalBytes: 0,
        percentage: 0,
        startTime,
        estimatedTimeRemaining: null,
        exportError: null,
      });

      try {
        const response = await DataHubClient.exportDataset(
          {
            publicId: props.dataSourceId,
            sortColumn: sort?.columnId,
            sortDirection: sort?.direction,
            filtersState: debouncedFilters,
            format,
            columns:
              settings?.export?.fields != null
                ? Object.entries(
                    settings.export.fields,
                  ).map(([field, headerName]) => ({ field, headerName }))
                : columnDefinition
                    .filter((col) => col.type !== "actions")
                    .map((col) => ({
                      field: col.field ?? "",
                      headerName: col.headerName ?? col.field ?? "",
                    })),
          },
          abortController.signal,
        );

        if (!response.ok) {
          let errorMessage: string;
          try {
            const errorJson = await response.json();
            errorMessage =
              errorJson.error || `Export failed (HTTP ${response.status})`;
          } catch {
            errorMessage = `Export failed (HTTP ${response.status})`;
          }
          throw new Error(errorMessage);
        }

        const totalRows = parseInt(
          response.headers.get("X-Total-Rows") || "0",
          10,
        );
        const pageSize = parseInt(
          response.headers.get("X-Page-Size") || "4000",
          10,
        );
        const totalPages = Math.ceil(totalRows / pageSize);
        const exportTimestamp =
          response.headers.get("X-Export-Timestamp") ||
          new Date().toISOString();

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No reader available");

        const chunks: BlobPart[] = [];
        let downloadedBytes = 0;
        let downloadedRows = 0;
        let dynamicAvgBytesPerRow = 200;
        const textDecoder = format === "csv" ? new TextDecoder() : null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(new Blob([value]));
          downloadedBytes += value.length;

          if (textDecoder) {
            const text = textDecoder.decode(value, { stream: true });
            const newlines = (text.match(/\r?\n/g) || []).length;
            downloadedRows += newlines;

            if (downloadedRows > 100) {
              dynamicAvgBytesPerRow = downloadedBytes / downloadedRows;
            }
          }

          const estimatedTotalBytes = totalRows * dynamicAvgBytesPerRow;

          const percentage = Math.min(
            99,
            Math.round((downloadedBytes / estimatedTotalBytes) * 100),
          );
          const elapsed = Date.now() - startTime;
          const bytesPerMs = downloadedBytes / elapsed;
          const remainingBytes = Math.max(
            0,
            estimatedTotalBytes - downloadedBytes,
          );
          const estimatedTimeRemaining = Math.round(
            remainingBytes / bytesPerMs / 1000,
          );

          let displayedRows: number;
          if (format === "csv") {
            displayedRows = Math.max(0, downloadedRows - 1);
          } else {
            const estimatedCurrentPage = Math.floor(
              (downloadedBytes / estimatedTotalBytes) * totalPages,
            );
            displayedRows = Math.min(
              totalRows,
              estimatedCurrentPage * pageSize,
            );
          }

          setExportProgress({
            isExporting: true,
            format,
            totalRows,
            downloadedRows: displayedRows,
            downloadedBytes,
            estimatedTotalBytes,
            percentage,
            startTime,
            estimatedTimeRemaining,
            exportError: null,
          });
        }

        if (format === "xlsx") {
          const zipBlob = new Blob(chunks, { type: "application/zip" });
          const url = URL.createObjectURL(zipBlob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${exportTimestamp}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          const csvBlob = new Blob(chunks, { type: "text/csv" });
          const zip = new JSZip();
          zip.file(`${exportTimestamp}.csv`, csvBlob);
          const zipBlob = await zip.generateAsync({ type: "blob" });

          const url = URL.createObjectURL(zipBlob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${exportTimestamp}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }

        setExportProgress((prev) => ({ ...prev, percentage: 100 }));
        exportResetTimeoutRef.current = setTimeout(() => {
          setExportProgress({
            isExporting: false,
            format: null,
            totalRows: 0,
            downloadedRows: 0,
            downloadedBytes: 0,
            estimatedTotalBytes: 0,
            percentage: 0,
            startTime: null,
            estimatedTimeRemaining: null,
            exportError: null,
          });
        }, 1500);
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") {
          const message = err.message;
          setExportProgress((prev) => ({
            ...prev,
            isExporting: false,
            exportError: message,
          }));
        }
      }
    },
    [
      props.dataSourceId,
      sort,
      debouncedFilters,
      columnDefinition,
      settings?.export?.fields,
    ],
  );

  const asyncFilterLoadOptionsMap = useMemo<
    Record<string, AsyncFilterLoadOptions>
  >(() => {
    const PAGE_SIZE = 100;
    const filterList = filtersDefinition
      .flatMap((f) => f.filters)
      .filter((f) => !!f.dataSource && f.type === "asyncMultiSelect");

    const map: Record<string, AsyncFilterLoadOptions> = {};
    const abortControllers: Record<string, AbortController | null> = {};

    for (const filter of filterList) {
      map[filter.code] = async (search, _, additional) => {
        const page = additional?.page ?? 1;
        const skip = (page - 1) * PAGE_SIZE;

        abortControllers[filter.code]?.abort();
        const controller = new AbortController();
        abortControllers[filter.code] = controller;

        const res = await fetchAsyncFilterData(
          String(filter.dataSource),
          search,
          skip,
          PAGE_SIZE + 1,
          filter.args,
          controller.signal,
        );

        const hasMore = res.data.length > PAGE_SIZE;
        const options = res.data.slice(0, PAGE_SIZE).map((item: any) => ({
          value: item.id,
          label: item.text,
        }));

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      };
    }

    return map;
  }, [filtersDefinition, fetchAsyncFilterData]);

  useEffect(() => {
    if (!props.dataSourceId || filtersDefinition.length === 0) {
      setFiltersDataSource({});
      return;
    }

    const filterList = filtersDefinition
      .flatMap((f) => f.filters)
      .filter((f) => !!f.dataSource && f.type !== "asyncMultiSelect");

    const fetchAllFilters = async () => {
      const results: Record<string, unknown[]> = {};

      try {
        const fetchFiltersData = filterList.map((f) => f.dataSource).join(",");

        const res = await DataHubClient.getDataset({
          publicId: props.dataSourceId,
          fetchFiltersData,
          _requestId: requestId,
        });
        const resFilterData = (res as unknown) as Record<string, unknown[]>;
        filterList.forEach((f: FilterDefinition) => {
          results[f.code] = resFilterData[String(f.dataSource)] ?? [];
        });
      } catch {
        filterList.forEach((f) => {
          results[f.code] = [];
        });
      }

      setFiltersDataSource(results);
    };

    fetchAllFilters();
  }, [props.dataSourceId, filtersDefinition, requestId]);

  useEffect(() => {
    DataHubClient.getDataset({
      publicId: props.dataSourceId,
      fetchDefinition: true,
      _requestId: requestId,
    })
      .then((res) => {
        setFiltersDefinition(res.filters);
        setColumnDefinition(res.columns ?? []);
        updateFiltersInitialValues(res.filters);
        updateSettings(res.settings);
        setDefinitionLoaded(true);
      })
      .catch(handleRequestError);
  }, [parseInitialValue, props.dataSourceId, updateFiltersInitialValues, requestId]);

  const value: DatasetContextValue = useMemo(
    () => ({
      columnDefinition,
      filtersDefinition,
      count,
      filteredCount,
      dataSourceId: props.dataSourceId,
      isLoading,
      hasError,
      traceId,
      sort,
      fetchRows,
      setSort,
      getFilterValue,
      setFilterValue,
      filtersState,
      filtersDataSource,
      resetFilters,
      settings,
      asyncFilterLoadOptions: asyncFilterLoadOptionsMap,
      setFiltersStateParam,
      getFiltersStateParam,
      filtersVersion,
      requiredFiltersSatisfied,
      requiredFiltersWarningMessage,
      selectedItemForDetail,
      setSelectedItemForDetail,
      exportData,
      cancelExport,
      dismissExportError,
      exportProgress,
      isDefinitionLoaded,
      refreshData,
    }),
    [
      columnDefinition,
      filtersDefinition,
      count,
      filteredCount,
      props.dataSourceId,
      isLoading,
      hasError,
      traceId,
      sort,
      fetchRows,
      getFilterValue,
      setFilterValue,
      filtersState,
      filtersDataSource,
      resetFilters,
      settings,
      asyncFilterLoadOptionsMap,
      setFiltersStateParam,
      getFiltersStateParam,
      filtersVersion,
      requiredFiltersSatisfied,
      requiredFiltersWarningMessage,
      selectedItemForDetail,
      setSelectedItemForDetail,
      exportData,
      cancelExport,
      dismissExportError,
      exportProgress,
      isDefinitionLoaded,
      refreshData,
    ],
  );

  return (
    <DatasetContext.Provider value={value}>
      {props.children}
    </DatasetContext.Provider>
  );
}
